import type { ApiClient, LegacyKuduResponseEnvelope } from '@/api/client.types';
import type {
  EditableQuestion,
  QuestionSectionKind,
  RawComponent,
  RawUpdateCoursePayload,
  UpdateCourseSavePayload,
} from '@/features/content/editor/editorTypes';

import {
  cloneValue,
  createUpdateCoursePayload,
  editableQuestionToRawQuestion,
  ensureQuestionSection,
  toRawQuestions,
  toSectionArray,
  toSnakeCaseValue,
  unwrapSavedComponent,
} from './questionServiceUtils';

export type CopyQuestionPosition =
  | {
      placement: 'end';
    }
  | {
      placement: 'after' | 'before';
      questionIndex: number;
    };

interface CopyQuestionsParams {
  courseId: string;
  position?: CopyQuestionPosition;
  questions: EditableQuestion[];
  scope: QuestionSectionKind;
  targetComponent: RawComponent;
}

function getQuestionInsertionIndex(
  existingQuestions: unknown[],
  position: CopyQuestionPosition | undefined,
) {
  if (!position || position.placement === 'end') {
    return existingQuestions.length;
  }

  const boundedIndex = Math.max(
    0,
    Math.min(Math.trunc(position.questionIndex), existingQuestions.length),
  );

  return position.placement === 'before'
    ? boundedIndex
    : Math.min(boundedIndex + 1, existingQuestions.length);
}

export function createComponentWithCopiedQuestions({
  position,
  questions,
  scope,
  targetComponent,
}: Omit<CopyQuestionsParams, 'courseId'>): RawComponent {
  if (questions.length === 0) {
    throw new Error('Select at least one question to copy.');
  }

  const component = toSnakeCaseValue(cloneValue(targetComponent)) as RawComponent;
  const sections = toSectionArray(component.sections);
  const questionSection = ensureQuestionSection(sections, scope);
  const existingQuestions = toRawQuestions(questionSection.questions);
  const copiedQuestions = questions.map((question) =>
    editableQuestionToRawQuestion(question),
  );
  const insertionIndex = getQuestionInsertionIndex(existingQuestions, position);
  const nextQuestions = [...existingQuestions];

  nextQuestions.splice(insertionIndex, 0, ...copiedQuestions);
  questionSection.questions = nextQuestions;
  component.sections = sections;

  return component;
}

export async function copyQuestions(
  client: ApiClient,
  {
    courseId,
    position,
    questions,
    scope,
    targetComponent,
  }: CopyQuestionsParams,
): Promise<RawComponent> {
  const component = createComponentWithCopiedQuestions({
    position,
    questions,
    scope,
    targetComponent,
  });
  const payload = createUpdateCoursePayload(courseId, component);

  const response = await client.invokeKuduMethod<
    RawUpdateCoursePayload | LegacyKuduResponseEnvelope<RawUpdateCoursePayload>,
    UpdateCourseSavePayload
  >('UpdateCourse', payload);

  return unwrapSavedComponent(response, component);
}
